/**
 * Stok Awal Bahan — saldo pembuka per bahan × gudang (Q134 stok berbasis ledger; Q135 "Stok Awal" = jenis transaksi
 * stok resmi). Backend: POST /api/inventory/stock/initial-stock (Owner, Kepala Cabang region, Admin Gudang region;
 * hanya sekali per pasangan bahan × gudang — sesudah itu perubahan lewat Adjustment). Angkanya data master resmi
 * go-live (K3): diisi staf/Owner, tidak dikarang sistem.
 *
 * Pola daftar seragam (28 Sep 2026; `docs/POLA_DAFTAR.md`): riwayat = daftar SERVER (buku stok bertanda
 * stok awal) berhalaman; pilihan bahan lewat `PemilihCari`.
 * ⛔ DULU riwayat DAN master bahan dipindai UTUH ke browser (`ambilSemua`) — juga untuk memeriksa
 *    "pasangan ini sudah punya stok awal". Pemeriksaan itu kini SATU pertanyaan ke server untuk
 *    pasangan yang dipilih (backend tetap menolak pasangan ganda — ini hanya peringatan dini).
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PackagePlus, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { formatDateTime, formatQty } from "../../lib/format";
import { listStockLedger, apiErrorMessage } from "../../lib/inventoryApi";
import PemilihCari from "../../components/etams/PemilihCari";
import { cariBahan, labelBahan } from "../../lib/sumberPilihan";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";

const kosong = { bahan: null, store_id: "", qty: "", notes: "" };
const JENIS = { reference_type: "INITIAL_STOCK" };

const KOLOM_CSV = [
  { kunci: "bahan", judul: "Bahan", ambil: (r) => labelBahan(r.material) },
  { kunci: "gudang", judul: "Gudang", ambil: (r) => r.store?.name || "" },
  { kunci: "qty_change", judul: "Jumlah" },
  { kunci: "satuan", judul: "Satuan", ambil: (r) => r.material?.unit_of_measure || "" },
  { kunci: "created_at", judul: "Dicatat", ambil: (r) => formatDateTime(r.created_at) },
  { kunci: "notes", judul: "Catatan" },
];

export default function StokAwal() {
  const [stores, setStores] = useState([]);
  const [riwayat, setRiwayat] = useState([]);
  const [total, setTotal] = useState(0);
  const [memuat, setMemuat] = useState(true);
  const [form, setForm] = useState(kosong);
  const [sudahAda, setSudahAda] = useState(false);
  const [sibuk, setSibuk] = useState(false);

  const daftar = useDaftar(riwayat, {
    server: { total, urutSah: ["created_at", "qty_change"] },
    urutAwal: { kunci: "created_at", arah: "desc" },
    saringan: {
      gudang: { label: "Gudang", param: "store_id", tampil: (v) => stores.find((s) => s.id === v)?.name || "…" },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  const bangunParams = useCallback((o = {}) => ({
    ...JENIS, ...paramSaringan, ...(cari && { q: cari }), limit, offset, sort, arah, ...o,
  }), [paramSaringan, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await listStockLedger(bangunParams({ offset: off, limit: n }))).data, [bangunParams]);

  const muatRiwayat = useCallback(async () => {
    setMemuat(true);
    try {
      const r = await listStockLedger(bangunParams());
      setRiwayat(r.data.items || []);
      setTotal(r.data.total ?? 0);
    } catch (e) { toast.error(apiErrorMessage(e, "Riwayat stok awal gagal dimuat.")); }
    finally { setMemuat(false); }
  }, [bangunParams]);

  useEffect(() => { muatRiwayat(); }, [muatRiwayat]);
  useEffect(() => {
    api.get("/stores").then((r) => setStores(r.data || []))
      .catch((e) => toast.error(apiErrorMessage(e, "Data gudang gagal dimuat.")));
  }, []);

  // Pasangan bahan × gudang yang dipilih sudah punya stok awal? — satu pertanyaan ke server.
  const idBahan = form.bahan?.id || "";
  useEffect(() => {
    setSudahAda(false);
    if (!idBahan || !form.store_id) return undefined;
    let batal = false;
    listStockLedger({ ...JENIS, material_id: idBahan, store_id: form.store_id, page: 1, page_size: 1 })
      .then((r) => { if (!batal) setSudahAda((r.data?.total ?? (r.data?.items || []).length) > 0); })
      .catch((e) => { if (!batal) toast.error(apiErrorMessage(e, "Pemeriksaan stok awal gagal.")); });
    return () => { batal = true; };
  }, [idBahan, form.store_id]);

  const simpan = async () => {
    setSibuk(true);
    try {
      await api.post("/inventory/stock/initial-stock", {
        material_id: idBahan, store_id: form.store_id, qty: Number(form.qty), notes: form.notes.trim() || null,
      });
      toast.success("Stok awal tercatat di buku stok.");
      setForm(kosong);
      await muatRiwayat();
    } catch (e) { toast.error(apiErrorMessage(e, "Stok awal gagal dicatat.")); }
    finally { setSibuk(false); }
  };

  return (
    <div className="space-y-6" data-testid="stok-awal-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><PackagePlus className="h-6 w-6" /> Stok Awal Bahan</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Saldo pembuka satu bahan di satu gudang, dicatat SEKALI sebelum ada gerakan stok lain. Sesudah itu
            perubahan lewat <Link className="underline" to="/inventory/adjustments">Adjustment Stok</Link> atau opname.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={muatRiwayat}><RefreshCw className="h-4 w-4 mr-1" /> Muat ulang</Button>
      </div>

      <Card className="p-4 space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="text-sm space-y-1">
            <span>Bahan</span>
            <PemilihCari value={form.bahan} onChange={(m) => setForm({ ...form, bahan: m })} cari={cariBahan}
                         label={labelBahan} placeholder="— pilih bahan —" testId="stok-awal-bahan" />
          </div>
          <label className="text-sm space-y-1">
            <span>Gudang</span>
            <select className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.store_id}
                    onChange={(e) => setForm({ ...form, store_id: e.target.value })} data-testid="stok-awal-gudang">
              <option value="">— pilih gudang —</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label className="text-sm space-y-1">
            <span>Jumlah {form.bahan?.unit_of_measure ? `(${form.bahan.unit_of_measure})` : ""}</span>
            <Input type="number" min="0" step="any" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })}
                   data-testid="stok-awal-qty" />
          </label>
          <label className="text-sm space-y-1">
            <span>Catatan (mis. hasil hitung fisik tanggal …)</span>
            <Textarea rows={1} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </label>
        </div>
        {sudahAda && (
          <p className="text-sm text-destructive" data-testid="stok-awal-sudah-ada">
            Bahan ini sudah punya stok awal di gudang tersebut — ubah lewat Adjustment Stok.
          </p>
        )}
        <Button disabled={sibuk || !idBahan || !form.store_id || !(Number(form.qty) > 0) || sudahAda}
                onClick={simpan} data-testid="stok-awal-simpan">Catat stok awal</Button>
      </Card>

      <Card className="p-4">
        <div className="font-medium mb-2">Stok awal yang sudah tercatat</div>
        <PalangDaftar daftar={daftar} placeholder="Cari kode / nama bahan / catatan" testId="stok-awal-palang"
                      ekspor={{ nama: "stok_awal_bahan", kolom: KOLOM_CSV, ambilHalaman }}>
          <select value={daftar.nilaiSaringan.gudang} onChange={(e) => daftar.setSaringan("gudang", e.target.value)}
                  className="h-9 rounded border px-2 text-sm" aria-label="Saring gudang" data-testid="stok-awal-saring-gudang">
            <option value="">Semua Gudang</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </PalangDaftar>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr><th className="text-left p-1">Bahan</th><th className="text-left p-1">Gudang</th>
                <ThUrut daftar={daftar} kunci="qty_change" className="p-1" align="right">Jumlah</ThUrut>
                <ThUrut daftar={daftar} kunci="created_at" className="p-1">Dicatat</ThUrut>
                <th className="text-left p-1">Catatan</th></tr>
            </thead>
            <tbody>
              {memuat && daftar.tampil.length === 0 && (
                <tr><td colSpan="5" className="p-2 text-muted-foreground">Memuat…</td></tr>
              )}
              {!memuat && daftar.tampil.length === 0 && (
                <tr><td colSpan="5" className="p-2 text-muted-foreground" data-testid="stok-awal-kosong">
                  {daftar.cari.trim() || daftar.saringanAktif.length ? "Tidak ada yang cocok." : "Belum ada."}
                </td></tr>
              )}
              {daftar.tampil.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="p-1">{r.material?.name || "—"}{r.material?.code ? <span className="text-xs text-muted-foreground"> ({r.material.code})</span> : null}</td>
                  <td className="p-1">{r.store?.name || "—"}</td>
                  <td className="p-1 text-right">{formatQty(r.qty_change)} {r.material?.unit_of_measure || ""}</td>
                  <td className="p-1">{formatDateTime(r.created_at)}</td>
                  <td className="p-1">{r.notes || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Paginasi daftar={daftar} testId="stok-awal-paginasi" />
      </Card>
    </div>
  );
}
